import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import LeftArrowIcon from '../../components/icons/LeftArrowIcon';
import SaveIcon from '../../components/icons/SaveIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import UndoIcon from '../../components/icons/UndoIcon';
import RedoIcon from '../../components/icons/RedoIcon';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const palette = [
  [0, 0, 0, 0], [255, 255, 255], [255, 33, 33], [255, 147, 196], [255, 129, 53], [255, 246, 9],
  [36, 156, 163], [120, 220, 82], [0, 63, 173], [135, 242, 255], [142, 46, 196], [164, 131, 159],
  [92, 64, 108], [229, 205, 196], [145, 70, 61], [0, 0, 0]
];

const CANVAS_SIZES = [16, 24, 32];
const PIXEL_SIZE = 20;

interface HistoryState {
  pixels: number[][];
}

const genres = [
  { value: 'fantasy', label: '판타지' }, { value: 'horror', label: '공포' }, { value: 'medieval', label: '중세' },
  { value: 'scifi', label: 'SF' }, { value: 'modern', label: '현대' }, { value: 'cute', label: '귀여운' },
  { value: 'monster', label: '몬스터' }, { value: 'robot', label: '로봇' }, { value: 'nature', label: '자연' },
  { value: 'food', label: '음식' }, { value: 'other', label: '기타' }
];

export const Editor: React.FC = () => {
  const [canvasSize, setCanvasSize] = useState(16);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pixels, setPixels] = useState<number[][]>(Array.from({ length: 16 }, () => Array(16).fill(0)));
  const [selectedColor, setSelectedColor] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState<HistoryState[]>([{ pixels: Array.from({ length: 16 }, () => Array(16).fill(0)) }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [varName, setVarName] = useState('mySprite');
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();

  const handleChangeSize = (size: number) => {
    setCanvasSize(size);
    const empty = Array.from({ length: size }, () => Array(size).fill(0));
    setPixels(empty);
    setHistory([{ pixels: empty }]);
    setHistoryIndex(0);
  };

  const drawPixel = (x: number, y: number, colorIdx: number) => {
    if (x < 0 || x >= canvasSize || y < 0 || y >= canvasSize) return;
    setPixels(prev => {
      if (prev[y][x] === colorIdx) return prev;
      const next = prev.map(row => [...row]);
      next[y][x] = colorIdx;
      return next;
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDrawing(true);
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    drawPixel(Math.floor((e.clientX - rect.left) / PIXEL_SIZE), Math.floor((e.clientY - rect.top) / PIXEL_SIZE), selectedColor);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing) return;
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    drawPixel(Math.floor((e.clientX - rect.left) / PIXEL_SIZE), Math.floor((e.clientY - rect.top) / PIXEL_SIZE), selectedColor);
  };
  const handleMouseUp = () => {
    setIsDrawing(false);
    setHistory(prev => [...prev.slice(0, historyIndex + 1), { pixels: pixels.map(row => [...row]) }]);
    setHistoryIndex(i => i + 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setPixels(history[historyIndex - 1].pixels.map(row => [...row]));
      setHistoryIndex(i => i - 1);
    }
  };
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setPixels(history[historyIndex + 1].pixels.map(row => [...row]));
      setHistoryIndex(i => i + 1);
    }
  };
  const handleClear = () => {
    const empty = Array.from({ length: canvasSize }, () => Array(canvasSize).fill(0));
    setPixels(empty);
    setHistory(prev => [...prev.slice(0, historyIndex + 1), { pixels: empty }]);
    setHistoryIndex(i => i + 1);
  };

  const handleRegister = async () => {
    if (!title.trim()) { toast.info('제목을 입력해주세요.'); return; }
    if (!genre) { toast.info('장르를 선택해주세요.'); return; }
    if (!currentUser) { toast.info('로그인이 필요합니다.'); return; }
    setIsSubmitting(true);
    try {
      let spriteString = '';
      for (let y = 0; y < canvasSize; y++) {
        let row = '';
        for (let x = 0; x < canvasSize; x++) row += pixels[y][x] === 0 ? '. ' : pixels[y][x].toString(16) + ' ';
        spriteString += row.trimEnd() + ' \n';
      }
      const code = `let ${varName} = sprites.create(img\`\n${spriteString}\`, SpriteKind.Player)`;
      const canvas = canvasRef.current;
      const previewUrl = canvas ? canvas.toDataURL() : '';
      await addDoc(collection(db, 'sprites'), {
        name: title, genre, code, authorId: currentUser.uid,
        authorName: currentUser.email?.split('@')[0] || '익명', createdAt: serverTimestamp(),
        likes: [], previewUrl, width: canvasSize, height: canvasSize
      });
      toast.success('갤러리에 등록되었습니다!');
      setTimeout(() => navigate('/makecodearcade/sprite-gallery'), 1200);
    } catch (e) {
      toast.error('등록 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () => {
    let spriteString = '';
    for (let y = 0; y < canvasSize; y++) {
      let row = '';
      for (let x = 0; x < canvasSize; x++) row += pixels[y][x] === 0 ? '. ' : pixels[y][x].toString(16) + ' ';
      spriteString += row.trimEnd() + ' \n';
    }
    const code = `let ${varName} = sprites.create(img\`\n${spriteString}\`, SpriteKind.Player)`;
    navigator.clipboard.writeText(code);
    toast.success('MakeCode Arcade 코드가 클립보드에 복사되었습니다!');
  };

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvasSize * PIXEL_SIZE, canvasSize * PIXEL_SIZE);
    for (let y = 0; y < canvasSize; y++) {
      for (let x = 0; x < canvasSize; x++) {
        const colorIdx = pixels[y][x];
        if (colorIdx === 0) continue;
        const color = palette[colorIdx];
        ctx.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
        ctx.fillRect(x * PIXEL_SIZE, y * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
      }
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    for (let i = 0; i <= canvasSize; i++) {
      ctx.beginPath();
      ctx.moveTo(i * PIXEL_SIZE, 0);
      ctx.lineTo(i * PIXEL_SIZE, canvasSize * PIXEL_SIZE);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * PIXEL_SIZE);
      ctx.lineTo(canvasSize * PIXEL_SIZE, i * PIXEL_SIZE);
      ctx.stroke();
    }
  }, [pixels, canvasSize]);

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto', padding: isMobile ? '0' : '32px 0' }}>
      <h2 style={{ fontFamily: 'var(--font-family-primary)', color: 'var(--Color-text-strong)', marginBottom: '16px' }}>스프라이트 에디터 (React/TSX)</h2>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        {CANVAS_SIZES.map(size => (
          <Button key={size} variant={canvasSize === size ? 'primary' : 'outline'} onClick={() => handleChangeSize(size)}>{size}x{size}</Button>
        ))}
      </div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ width: '180px' }}><InputField label="제목" value={title} onChange={setTitle} width="100%" /></div>
        <div style={{ minWidth: '120px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#161616' }}>장르</label>
          <select value={genre} onChange={e => setGenre(e.target.value)} style={{ width: '100%', height: '40px', padding: '8px 12px', border: '1.2px solid #F0F0F0', borderRadius: '8px', fontSize: '14px', fontFamily: 'var(--font-family-primary)', backgroundColor: 'white' }}>
            <option value="">선택</option>
            {genres.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        <div style={{ width: '120px' }}><InputField label="변수명" value={varName} onChange={v => setVarName(v.replace(/\W/g, ''))} width="100%" /></div>
        <Button variant="primary" onClick={handleRegister} disabled={isSubmitting}>등록</Button>
        <Button variant="outline" onClick={handleSave}>코드 복사</Button>
        <Button variant="line" leftIcon={<LeftArrowIcon width={20} height={20} />} onClick={() => navigate('/makecodearcade/sprite-gallery')}>스프라이트 갤러리로</Button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button type="button" onClick={handleUndo} disabled={historyIndex === 0} title="실행 취소" style={{ padding: '8px', border: 'none', background: 'transparent', cursor: historyIndex === 0 ? 'not-allowed' : 'pointer', opacity: historyIndex === 0 ? 0.5 : 1 }}><UndoIcon width={24} height={24} /></button>
          <button type="button" onClick={handleRedo} disabled={historyIndex === history.length - 1} title="다시 실행" style={{ padding: '8px', border: 'none', background: 'transparent', cursor: historyIndex === history.length - 1 ? 'not-allowed' : 'pointer', opacity: historyIndex === history.length - 1 ? 0.5 : 1 }}><RedoIcon width={24} height={24} /></button>
          <button type="button" onClick={handleClear} title="전체 지우기" style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}><TrashIcon width={24} height={24} color="#161616" /></button>
        </div>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {palette.map((color, idx) => (
            <button key={idx} type="button" title={`색상 ${idx}`} onClick={() => setSelectedColor(idx)} style={{ width: 28, height: 28, border: `2px solid ${selectedColor === idx ? 'var(--Color-fill-brand-color)' : '#ccc'}`, background: idx === 0 ? 'transparent' : `rgb(${color[0]},${color[1]},${color[2]})`, borderRadius: 4, cursor: 'pointer', backgroundImage: idx === 0 ? 'repeating-conic-gradient(#eee 0% 25%, #ccc 0% 50%) 50% / 12px 12px' : undefined }} />
          ))}
        </div>
        <div style={{ background: 'repeating-conic-gradient(#eee 0% 25%, #ccc 0% 50%) 50% / 32px 32px', padding: 24, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <canvas ref={canvasRef} width={canvasSize * PIXEL_SIZE} height={canvasSize * PIXEL_SIZE} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} style={{ imageRendering: 'pixelated', background: 'transparent', border: '1px solid #aaa' }} />
        </div>
      </div>
      </div>
    </div>
  );
};
